import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "@/sections/Header";
import FooterSection from "@/sections/Footer";
import { AcademyHero } from "@/components/academy/AcademyHero";
import { TopicStrip } from "@/components/academy/TopicStrip";
import { AudienceCards } from "@/components/academy/AudienceCards";
import { FindYourPath } from "@/components/academy/FindYourPath";
import { FreeLesson } from "@/components/academy/FreeLesson";
import { ProgramExplorer } from "@/components/academy/ProgramExplorer";
import { ProgramSheet } from "@/components/academy/ProgramSheet";
import { LearningMethod, TeamsSection } from "@/components/academy/AcademyPillars";
import { LaunchOffers } from "@/components/academy/LaunchOffers";
import { AcademyClosing, AcademyFaq, CareersBridge } from "@/components/academy/AcademyConnect";
import { InquiryDialog } from "@/components/academy/inquiry/InquiryDialog";
import { academyRequest, goToSection, track } from "@/lib/academy/actions";
import { usePageMeta } from "@/lib/usePageMeta";
import { FOCUS } from "@/lib/academy/ui";
import { ACADEMY_LAUNCHED } from "@/lib/academy/launch";
import AcademyTeaserPage from "@/pages/AcademyTeaserPage";

const SUBNAV = [["start-here", "Start here"], ["free-lesson", "Free lesson"], ["programs", "Programs"], ["method", "Method"], ["teams", "Teams"], ["offers", "Offers"], ["careers", "Careers"], ["faq", "FAQ"]];

const META = {
  title: "DCYB Academy · Learn the Industry Behind AI",
  description: "Learn how data centers work and explore the people and businesses behind digital infrastructure. Try a free interactive lesson or discover guided learning for yourself and your team.",
  path: "/academy",
  image: "/dcyb-academy-og.png",
};

export default function AcademyPage() {
  return ACADEMY_LAUNCHED ? <AcademyFullPage /> : <AcademyTeaserPage />;
}

function AcademyFullPage() {
  const location = useLocation();
  const [programId, setProgramId] = useState(null);
  const programOpener = useRef(null);
  const [pathPreset, setPathPreset] = useState(null);
  const [offersState, setOffersState] = useState({ status: "loading", offers: [] });
  usePageMeta(META);

  const loadOffers = useCallback(() => {
    setOffersState((previous) => ({ ...previous, status: "loading" }));
    academyRequest("/offers").then((body) => setOffersState({ status: "ready", offers: body.offers })).catch(() => setOffersState({ status: "error", offers: [] }));
  }, []);
  useEffect(() => { loadOffers(); track("academy_page_viewed"); }, [loadOffers]);

  useEffect(() => {
    if (!location.hash) { window.scrollTo(0, 0); return undefined; }
    let active = true;
    const jump = () => { if (active) document.getElementById(location.hash.slice(1))?.scrollIntoView({ block: "start", behavior: "instant" }); };
    document.fonts.ready.then(jump);
    const settle = setTimeout(jump, 700);
    return () => { active = false; clearTimeout(settle); };
  }, [location.hash]);

  const openProgram = (id) => { programOpener.current = document.activeElement; setProgramId(id); track("academy_program_opened", { program: id }); };
  const choosePath = (preset, card) => { setPathPreset({ ...preset, nonce: Date.now() }); track("academy_audience_selected", { audience: card }); setTimeout(() => goToSection("find-your-path"), 0); };

  return (
    <main data-testid="academy-page" className="academy-page relative">
      <Header />
      <div className="academy-brand">
      <AcademyHero />
      <TopicStrip />
      <nav aria-label="Academy sections" data-testid="academy-subnav" className="sticky top-[68px] md:top-[72px] z-30 bg-white/95 backdrop-blur border-b hairline">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex gap-1 overflow-x-auto scrollbar-none py-2">
          {SUBNAV.map(([id, label]) => <a key={id} href={`#${id}`} data-testid={`academy-subnav-${id}`} className={`shrink-0 px-3 py-2 font-mono-label text-[10px] text-[#3C4450] hover:text-[#160907] hover:bg-[#FFF6F1] transition-colors whitespace-nowrap ${FOCUS}`}>{label}</a>)}
        </div>
      </nav>
      <AudienceCards onSelect={choosePath} />
      <FindYourPath preset={pathPreset} onOpenProgram={openProgram} />
      <FreeLesson onOpenProgram={openProgram} />
      <ProgramExplorer onOpenProgram={openProgram} />
      <LearningMethod />
      <TeamsSection />
      <LaunchOffers offersState={offersState} onRetry={loadOffers} />
      <CareersBridge />
      <AcademyFaq />
      <AcademyClosing />
      </div>
      <FooterSection />
      <ProgramSheet programId={programId} openerRef={programOpener} onClose={() => setProgramId(null)} />
      <InquiryDialog offers={offersState.offers} />
    </main>
  );
}
